import { supabase } from '../supabaseClient';

export interface FreelancerProfile {
  id: string;
  full_name: string;
  company: string | null;
  bio: string | null;
  skills: string[];
  rating_avg: number;
  rating_cnt: number;
  hourly_rate: number | null;
  location: string | null;
  verified: boolean;
  completed_jobs: number;
  total_earnings: number;
  avatar_url: string | null;
  portfolio_url: string | null;
  role: 'FREELANCER' | 'AGENCY';
  created_at: string;
}

export interface FreelancerSearchParams {
  query?: string;
  skills?: string[];
  minRating?: number;
  maxHourlyRate?: number;
  location?: string;
  limit?: number;
  offset?: number;
}

export async function searchFreelancers(_params: FreelancerSearchParams = {}): Promise<{
  data: FreelancerProfile[];
  error: string | null;
  count: number;
}> {
  try {
    console.log('Searching for ALL freelancers and agencies...');
    
    // Get ALL freelancers and agencies without any restrictions
    const { data, error, count } = await supabase
      .from('profiles')
      .select('*', { count: 'exact' })
      .in('role', ['FREELANCER', 'AGENCY']);

    console.log('All freelancers/agencies result:', { data, error, count });

    if (error) {
      console.error('Error fetching freelancers:', error);
      return { data: [], error: error.message, count: 0 };
    }

    console.log(`Found ${data?.length || 0} freelancers/agencies`);
    return { data: data || [], error: null, count: count || 0 };
  } catch (error) {
    console.error('Error in searchFreelancers:', error);
    return { data: [], error: 'Неочаквана грешка при търсене', count: 0 };
  }
}

export async function getFreelancerById(id: string): Promise<{
  data: FreelancerProfile | null;
  error: string | null;
}> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .in('role', ['FREELANCER', 'AGENCY'])
      .eq('status', 'ACTIVE')
      .single();

    if (error) {
      console.error('Error getting freelancer:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (error) {
    console.error('Error in getFreelancerById:', error);
    return { data: null, error: 'Неочаквана грешка при зареждане на профил' };
  }
}

// Test function to check all profiles
export async function getAllProfiles(): Promise<{
  data: any[];
  error: string | null;
}> {
  try {
    console.log('Testing: Getting all profiles...');
    const { data, error } = await supabase
      .from('profiles')
      .select('*');

    console.log('All profiles result:', { data, error });
    return { data: data || [], error: error?.message || null };
  } catch (error) {
    console.error('Error in getAllProfiles:', error);
    return { data: [], error: 'Неочаквана грешка при зареждане на профили' };
  }
}

// Test function to check Supabase connection
export async function testSupabaseConnection(): Promise<{
  success: boolean;
  error: string | null;
}> {
  try {
    console.log('Testing Supabase connection...');
    const { error } = await supabase
      .from('profiles')
      .select('count')
      .limit(1);

    if (error) {
      console.error('Supabase connection test failed:', error);
      return { success: false, error: error.message };
    }

    console.log('Supabase connection test successful');
    return { success: true, error: null };
  } catch (error) {
    console.error('Error in testSupabaseConnection:', error);
    return { success: false, error: 'Неочаквана грешка при тестване на връзката' };
  }
}

export async function getPopularSkills(): Promise<{
  data: string[];
  error: string | null;
}> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('skills')
      .in('role', ['FREELANCER', 'AGENCY'])
      .eq('status', 'ACTIVE')
      .not('skills', 'is', null);

    if (error) {
      console.error('Error getting popular skills:', error);
      return { data: [], error: error.message };
    }

    // Count skill occurrences
    const skillCounts: { [key: string]: number } = {};
    data?.forEach(profile => {
      profile.skills?.forEach((skill: string) => {
        skillCounts[skill] = (skillCounts[skill] || 0) + 1;
      });
    });

    // Sort by count and return top skills
    const popularSkills = Object.entries(skillCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 20)
      .map(([skill]) => skill);

    return { data: popularSkills, error: null };
  } catch (error) {
    console.error('Error in getPopularSkills:', error);
    return { data: [], error: 'Неочаквана грешка при зареждане на умения' };
  }
}
